<template>
  <div class="pa-plans">
    <div v-if="!initialLoadDone" class="pa-plans__loading">Loading content plans…</div>

    <div v-else-if="silos.length === 0" class="pa-panel pa-plans__empty">
      <h3>No content plans yet</h3>
      <p>Your content plans are created by your setup audit. {{ emptyHint }}</p>
    </div>

    <template v-else>
      <div class="pa-chips" role="tablist" aria-label="Filter content plans">
        <button
          v-for="chip in chips"
          :key="chip.id"
          type="button"
          role="tab"
          class="pa-chip"
          :class="{ 'pa-chip--sel': activeFilter === chip.id }"
          :aria-selected="activeFilter === chip.id"
          @click="activeFilter = chip.id"
        >
          {{ chip.label }}<span class="pa-chip__n">{{ chip.count }}</span>
        </button>
      </div>

      <p class="pa-plans__info">The Content Plan area lists the content clusters we have planned for you.</p>
      <p class="pa-plans__sub">
        A content cluster is made up of a main page / keyword and recommended supporting articles. Topics for
        supporting articles are chosen based on relevancy, search volume and your site's ability to quickly rank
        for them. Your SEO Manager schedules clusters into package months; each month we bill your card
        automatically and the next scheduled cluster moves to In progress.
      </p>

      <div v-if="showProductionAdmin" class="pa-plans__admin">
        <div class="pa-plans__admin-head">
          <span class="pa-plans__admin-label">Admin</span>
          <b>{{ currentMonthName }} production</b>
        </div>
        <div class="pa-plans__admin-line">
          {{ currentCycleOrdered.length }} of {{ currentCycleSilos.length }} scheduled
          {{ currentCycleSilos.length === 1 ? 'plan' : 'plans' }} {{ currentCycleSilos.length === 1 ? 'has' : 'have' }}
          an included order &middot; package cap {{ packageInfo.silos_per_month }}/month
        </div>
        <div v-if="currentCycleUnselected.length" class="pa-plans__admin-warn">
          <i class="material-icons" aria-hidden="true">warning</i>
          {{ currentCycleUnselected.length }} scheduled
          {{ currentCycleUnselected.length === 1 ? 'plan has' : 'plans have' }} no keyword selection and will be skipped
        </div>
        <button
          type="button"
          class="pa-btn pa-btn--out"
          :disabled="startProductionDisabled"
          @click="showStartProductionConfirm = true"
        >
          Start production for {{ currentMonthName }} (admin)
        </button>
      </div>

      <!-- design pa-app.jsx Modal: round icon, title, muted body, Cancel + orange confirm -->
      <div
        v-if="showStartProductionConfirm"
        class="pa-ovl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pa-start-production-title"
        @click.self="showStartProductionConfirm = false"
      >
        <div class="pa-cmodal">
          <div class="pa-cmodal__icon"><i class="material-icons" aria-hidden="true">play_arrow</i></div>
          <h3 id="pa-start-production-title" class="pa-cmodal__title">Start production for {{ currentMonthName }}?</h3>
          <p class="pa-cmodal__body">
            Creates one included content order per scheduled plan that has a keyword selection
            (up to {{ packageInfo.silos_per_month }}), locks those plans and opens the writer tasks in
            Asana. Plans without a selection are skipped.
          </p>
          <div class="pa-cmodal__acts">
            <button type="button" class="pa-btn pa-btn--out" :disabled="adminBusy" @click="showStartProductionConfirm = false">Cancel</button>
            <button type="button" class="pa-btn pa-btn--orange" :disabled="adminBusy" @click="startProduction">
              Yes, start production
            </button>
          </div>
        </div>
      </div>

      <div class="pa-plans__grid">
        <content-plan-card
          v-for="silo in displayedSilos"
          :key="silo.id"
          :silo="silo"
          :package-info="packageInfo"
          :current-cycle="currentCycle"
          :cycle-anchor-at="cycleAnchorAt"
          :refresh-data="fetchSilos"
        />
        <div v-if="displayedSilos.length === 0" class="pa-panel pa-plans__empty pa-plans__empty--filter">
          <h3>Nothing here yet</h3>
          <p>No content plan matches this filter.</p>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { mixin } from '@/components/mixin'
import ContentPlanCard from './ContentPlanCard.vue'
import { parseWhiteGloveOrdersResponse } from '@/utils/whiteGloveSiteList'
import {
  POP_AUTHORITY_LIVE_SUBSCRIPTION_STATUSES,
  POP_AUTHORITY_PROJECT_STATUS,
  POP_AUTHORITY_START_PRODUCTION_SKIP_REASONS,
  popAuthorityCurrentCycle,
  popAuthorityCycleAnchorAt,
  popAuthorityCycleMonthName,
  popAuthorityOrderIsIncluded,
  popAuthorityPackageFromStatus,
  popAuthorityPlanHasSelection,
  popAuthorityPlanOrderedOutsidePackage,
  popAuthorityPlanState,
  popAuthoritySiteStatus,
} from '@/utils/popAuthority'

const FILTERS = [
  { id: 'all', label: 'All plans' },
  { id: 'scheduled', label: 'Scheduled' },
  { id: 'in_progress', label: 'In progress' },
  { id: 'completed', label: 'Complete' },
]

/**
 * POP Authority "Content plans" tab (design pa-tabs.jsx PlansTab): the silos created by the audit
 * with their package month, state chips and the admin scheduler. The site status row (package,
 * expected delivery) comes from the layout; a fallback fetch covers direct loads.
 */
export default {
  name: 'ContentPlansPopAuthority',
  components: { ContentPlanCard },
  mixins: [mixin],
  inject: {
    getPopAuthoritySiteStatus: { default: null },
    refreshWhiteGloveOrders: { default: null },
  },
  data () {
    return {
      silos: [],
      initialLoadDone: false,
      activeFilter: 'all',
      fetchedStatus: null,
      adminBusy: false,
      showStartProductionConfirm: false,
    }
  },
  computed: {
    siteId () {
      return Number(this.$route.params.siteId)
    },
    status () {
      const fromLayout = typeof this.getPopAuthoritySiteStatus === 'function' ? this.getPopAuthoritySiteStatus() : null
      return fromLayout || this.fetchedStatus
    },
    packageInfo () {
      return popAuthorityPackageFromStatus(this.status)
    },
    /** Current package month from the subscription (`status.package.current_cycle`). */
    currentCycle () {
      return popAuthorityCurrentCycle(this.status)
    },
    /** First paid invoice date: anchors every campaign month label to a calendar month. */
    cycleAnchorAt () {
      return popAuthorityCycleAnchorAt(this.status)
    },
    isMissionControlSession () {
      return Boolean(this.$store.state.selectedSubAccountRelation?.isMissionControlSubAccount)
    },
    isAdmin () {
      return this.isMissionControlSession && Boolean(this.$store.state.isWhiteGloveAdminMode)
    },
    packageIsLive () {
      const status = (this.status && this.status.package && this.status.package.subscription_status) || ''
      return POP_AUTHORITY_LIVE_SUBSCRIPTION_STATUSES.includes(status)
    },
    /** Admin production panel: only once a package month is actually being billed. */
    showProductionAdmin () {
      return this.isAdmin && this.packageIsLive && !this.packageInfo.provisional
    },
    currentMonthName () {
      return popAuthorityCycleMonthName(this.currentCycle, this.cycleAnchorAt) || `month ${this.currentCycle}`
    },
    currentCycleSilos () {
      // a plan ordered outside a package can never be produced by the month, even when it still
      // carries a schedule from before: it must not keep "Start production" enabled
      return this.silos.filter(
        (s) => Number(s.paScheduledCycle) === this.currentCycle && !popAuthorityPlanOrderedOutsidePackage(s)
      )
    },
    currentCycleOrdered () {
      return this.currentCycleSilos.filter((s) => popAuthorityOrderIsIncluded(s.content_report_order))
    },
    currentCycleUnselected () {
      return this.currentCycleSilos.filter(
        (s) => !popAuthorityOrderIsIncluded(s.content_report_order) && !popAuthorityPlanHasSelection(s)
      )
    },
    startProductionDisabled () {
      return this.adminBusy
        || this.currentCycleSilos.length === 0
        || this.currentCycleOrdered.length === this.currentCycleSilos.length
    },
    emptyHint () {
      const key = this.status && this.status.status_key
      const eta = this.status && this.status.template_context && this.status.template_context.est_delivery_date
      const auditRunning = [
        POP_AUTHORITY_PROJECT_STATUS.SETUP_IN_PROGRESS,
        POP_AUTHORITY_PROJECT_STATUS.CALL_SCHEDULED,
      ].includes(key)
      if (auditRunning && eta && eta !== 'TBD') return `Expected here by ${eta}.`
      return 'They appear 3 to 5 business days after setup is complete.'
    },
    stateOf () {
      const map = {}
      this.silos.forEach((s) => { map[s.id] = popAuthorityPlanState(s) })
      return map
    },
    chips () {
      const count = (id) => (id === 'all'
        ? this.silos.length
        : this.silos.filter((s) => this.stateOf[s.id] === id).length)
      return FILTERS.map((f) => ({ ...f, count: count(f.id) }))
    },
    filteredSilos () {
      if (this.activeFilter === 'all') return this.silos
      return this.silos.filter((s) => this.stateOf[s.id] === this.activeFilter)
    },
    displayedSilos () {
      const list = [...this.filteredSilos]
      list.sort((a, b) => {
        const ca = Number(a.paScheduledCycle) || Number.MAX_SAFE_INTEGER
        const cb = Number(b.paScheduledCycle) || Number.MAX_SAFE_INTEGER
        if (ca !== cb) return ca - cb
        const ta = new Date(a.updatedAt || a.createdAt || 0).getTime()
        const tb = new Date(b.updatedAt || b.createdAt || 0).getTime()
        return tb - ta
      })
      return list
    },
  },
  watch: {
    '$store.state.refreshDataAfterCartPurchase' () {
      this.fetchSilos({ silent: true })
    },
    siteId (next, prev) {
      if (next !== prev && prev !== undefined) {
        this.fetchSilos()
        this.fetchStatusIfNeeded()
      }
    },
  },
  mounted () {
    this.fetchSilos()
    this.fetchStatusIfNeeded()
  },
  methods: {
    fetchSilos (opts = {}) {
      const silent = opts.silent === true
      if (!silent) this.$store.commit('showLoading')
      return this.$store
        .dispatch('getSilos', {
          siteId: this.siteId,
          paginate: 0,
          // cards read content_report_order + the server-computed label / count fields
          includeKeywords: 0,
          includeAiAndPageRunResult: 0,
          includePage: 0,
        })
        .then((response) => {
          this.silos = (response.data && response.data.data) || []
        })
        .catch(() => {
          this.silos = []
        })
        .finally(() => {
          if (!silent) this.$store.commit('hideLoading')
          this.initialLoadDone = true
        })
    },
    /** Admin: (re)run the production of the current package month (e.g. a plan scheduled late). */
    startProduction () {
      if (this.adminBusy) return Promise.resolve()
      this.adminBusy = true
      this.$store.commit('showLoading')
      return this.$store
        .dispatch('startPopAuthorityCycleProduction', { siteId: this.siteId, cycleNumber: this.currentCycle })
        .then((response) => {
          const data = response && response.data
          if (!data || data.status !== 'SUCCESS') {
            this.$notify({ group: 'info', type: 'error', text: (data && data.msg) || 'Could not start production' })
            return null
          }
          const production = data.production || {}
          const started = (production.started || []).length
          this.$notify({
            group: 'info',
            type: started ? 'success' : 'warn',
            text: started
              ? `Production started for ${started} ${started === 1 ? 'plan' : 'plans'}`
              : 'No plans started',
          })
          const skipped = production.skipped || []
          if (skipped.length) {
            const byReason = {}
            skipped.forEach((item) => {
              const label = POP_AUTHORITY_START_PRODUCTION_SKIP_REASONS[item.reason] || item.reason
              byReason[label] = (byReason[label] || 0) + 1
            })
            const summary = Object.keys(byReason).map((label) => `${byReason[label]} ${label}`).join(', ')
            this.$notify({ group: 'info', type: 'warn', text: `Skipped ${skipped.length}: ${summary}` })
          }
          return data
        })
        .catch((error) => {
          const msg = error?.response?.data?.msg || 'Could not start production'
          this.$notify({ group: 'info', type: 'error', text: msg })
          return null
        })
        .finally(() => {
          this.showStartProductionConfirm = false
          this.$store.commit('hideLoading')
          this.adminBusy = false
          const tasks = [this.fetchSilos({ silent: true })]
          if (typeof this.refreshWhiteGloveOrders === 'function') tasks.push(this.refreshWhiteGloveOrders())
          return Promise.all(tasks)
        })
    },
    /** Direct loads outside the layout (or before it finished) fetch the status row themselves. */
    fetchStatusIfNeeded () {
      if (typeof this.getPopAuthoritySiteStatus === 'function' && this.getPopAuthoritySiteStatus()) return
      this.$store.dispatch('getWhiteGloveOrders')
        .then((response) => {
          const parsed = parseWhiteGloveOrdersResponse(response)
          this.fetchedStatus = popAuthoritySiteStatus(this.siteId, parsed.siteProjectStatuses)
        })
        .catch(() => {})
    },
  },
}
</script>

<style lang="scss" scoped>
@import './setup/setup.scss';
@import './setup/designModal.scss';

.pa-plans__admin {
  border: 1px dashed #c9a3d6;
  background: #fbf3fd;
  border-radius: 10px;
  padding: 12px 16px;
  margin-bottom: 18px;
  max-width: 78ch;
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 0.85rem;

  &-head {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &-label {
    text-transform: uppercase;
    letter-spacing: 0.04em;
    font-size: 0.7rem;
    font-weight: 800;
    color: #7a3e8f;
    background: #fff;
    border-radius: 999px;
    padding: 2px 8px;
  }

  &-line {
    color: var(--v-gray2Color-base, #757575);
  }

  &-warn {
    display: flex;
    align-items: center;
    gap: 6px;
    color: var(--v-redColor-base, #e53935);

    .material-icons {
      font-size: 16px;
    }
  }

  .pa-btn {
    align-self: flex-start;
  }
}

.pa-plans {
  padding: 4px 0 40px;
  color: $pa-teal;
  font-size: 0.9rem;

  &__loading {
    font-size: 0.95rem;
    margin: 16px 0;
  }

  &__empty {
    max-width: 640px;

    h3 {
      margin: 0;
      font-size: 1.05rem;
      font-weight: 800;
    }

    p {
      color: $pa-muted;
      margin: 6px 0 0;
    }

    &--filter {
      grid-column: 1 / -1;
    }
  }

  &__info {
    font-weight: 800;
    font-size: 1rem;
    margin: 0 0 4px;
  }

  /* pa.css .pa-plansub: the cards follow directly (the design has no package strip here) */
  &__sub {
    color: $pa-muted;
    font-size: 0.88rem;
    max-width: 78ch;
    margin: 0 0 18px;
  }

  &__grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;

    @media (max-width: 1100px) {
      grid-template-columns: 1fr;
    }
  }
}

.pa-panel {
  border: 1px solid $pa-border-soft;
  border-radius: 12px;
  background: #fff;
  padding: 18px 20px;
}

/* .pa-chips / .pa-chip live in setup/setup.scss: the deliverables table uses them too */
</style>
